import React, { useMemo, useState } from 'react';
import { FlatList, Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { hasFirebase, useDeals, useSummary } from '../../src/data';
import { GROUPS, groupOf, price, timeAgo, type Group } from '../../src/format';
import { Logo } from '../../src/brand';
import { D } from '../../src/theme';
import type { Deal } from '../../src/types';

/**
 * 홈 피드 — 인더스트리얼 방향.
 *
 * 딜 앱은 사진으로 훑는 물건이라 2열 그리드로 간다. 사진을 못 구한 딜은 버리지 않고
 * 점수를 크게 채운다. 커뮤니티 글에서 시작하는 구조라 사진이 늘 있을 수는 없고,
 * 사진 없는 딜을 빼면 피드의 절반이 사라진다.
 */
export default function Home() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const deals = useDeals();
  const summary = useSummary();
  const [group, setGroup] = useState<Group>('전체');
  const [cols, setCols] = useState<2 | 3>(2);
  const shown = useMemo(() => (group === '전체' ? deals : deals.filter((d) => groupOf(d) === group)), [deals, group]);
  const refreshed = summary?.run_at ? timeAgo(summary.run_at) : '대기 중';

  return (
    <View style={{ flex: 1, backgroundColor: D.ground }}>
      <View style={[s.head, { paddingTop: insets.top + 14 }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
          <Logo size={26} tone={D.ink} />
          <Text style={s.brand}>딜헌터</Text>
        </View>
        <Text style={s.brandSub}>커뮤니티 특가를 모아 점수로 거릅니다</Text>
      </View>

      <Pressable onPress={() => router.push('/(tabs)/search')} style={({ pressed }) => [s.ask, pressed && { opacity: 0.9 }]}>
        <Text style={s.askLabel}>AI 에게 맡기기</Text>
        <Text style={s.askBody} numberOfLines={1}>
          찾는 걸 말하듯 적으면 뜰 때 알려드려요
        </Text>
      </Pressable>

      <View style={s.bar}>
        <View style={{ gap: 1 }}>
          <Text style={s.kicker}>{refreshed} 갱신 · 70점 이상</Text>
          <Text style={s.h1}>오늘의 딜</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Pressable onPress={() => setCols(cols === 2 ? 3 : 2)} style={s.colBtn} hitSlop={6}>
            <Text style={s.colBtnText}>{cols}열</Text>
          </Pressable>
          <Text style={s.count}>{shown.length}건</Text>
        </View>
      </View>

      <FlatList
        data={shown}
        key={cols}
        keyExtractor={(d) => d.id}
        numColumns={cols}
        columnWrapperStyle={{ gap: 8, paddingHorizontal: 16 }}
        contentContainerStyle={{ gap: 8, paddingBottom: 24 }}
        ListHeaderComponent={
          <View style={s.nav}>
            {GROUPS.map((g) => (
              <Pressable key={g} onPress={() => setGroup(g)} style={[s.chip, g === group && s.chipOn]}>
                <Text style={[s.chipText, g === group && s.chipTextOn]}>{g}</Text>
              </Pressable>
            ))}
          </View>
        }
        ListEmptyComponent={
          <View style={s.empty}>
            <Text style={s.emptyTitle}>{deals.length ? '이 분류엔 아직 없어요' : '딜을 불러오는 중'}</Text>
            <Text style={s.emptySub}>
              {deals.length ? '다른 분류를 골라 보세요.' : '수집기가 5분마다 70점 이상 딜을 올려요.'}
            </Text>
          </View>
        }
        ListFooterComponent={
          <View style={{ paddingHorizontal: 16, paddingTop: 14, gap: 8 }}>
            {!hasFirebase ? <Text style={s.demo}>Firebase 미연결 · 수집기의 마지막 결과를 예시로 보여줘요</Text> : null}
            <Text style={s.disclosure}>
              커뮤니티 제보가를 모아 다시 채점한 목록입니다. 판매 페이지에서 옵션과 결제 금액을 확인하세요.
            </Text>
            <Text style={s.disclosure}>
              이 서비스는 제휴마케팅이 포함된 광고로 커미션을 지급 받습니다. 쿠팡 파트너스 활동을 포함하며 상품 가격에는
              영향을 주지 않습니다. 판매처가 직접 운영하는 공식 서비스가 아닌 독립 큐레이션 서비스입니다.
            </Text>
          </View>
        }
        renderItem={({ item }) => (
          <Tile deal={item} cols={cols} onPress={() => router.push(`/deal/${item.id}`)} />
        )}
      />
    </View>
  );
}

/** 시장가를 아는 딜만 할인율을 돌려준다. 모르면 화면에 자리를 만들지 않는다. */
function discount(d: Deal): number | null {
  if (!d.market_price || !d.effective_price) return null;
  const pct = Math.round((1 - d.effective_price / d.market_price) * 100);
  return pct >= 5 ? pct : null;
}

function Tile({ deal, cols, onPress }: { deal: Deal; cols: 2 | 3; onPress: () => void }) {
  const [broken, setBroken] = useState(false);
  const thumb = !broken ? deal.thumb_url : null;
  const off = discount(deal);
  const tight = cols === 3;
  return (
    <Pressable onPress={onPress} style={({ pressed }) => [s.tile, pressed && { opacity: 0.9 }]}>
      <View style={s.shot}>
        {thumb ? (
          <>
            <Image source={{ uri: thumb }} style={{ width: '100%', height: '100%' }} resizeMode="cover" onError={() => setBroken(true)} />
            {/* 사진이 있으면 점수는 모서리 배지로 */}
            <View style={s.scoreTag}>
              <Text style={s.scoreTagText}>{Math.round(deal.score)}</Text>
            </View>
          </>
        ) : (
          // 사진을 못 구한 딜은 점수로 칸을 채운다. 이때 모서리 배지는 같은 말이라 안 붙인다
          <View style={s.noShot}>
            <Text style={[s.scoreNum, tight && { fontSize: 30 }]}>{Math.round(deal.score)}</Text>
            <Text style={s.scoreUnit}>점</Text>
          </View>
        )}
      </View>

      <View style={s.meta}>
        <Text style={s.mall} numberOfLines={1}>
          {deal.mall ?? '판매처 미확인'}
        </Text>
        <Text style={[s.name, tight && { fontSize: 11.5, lineHeight: 15 }]} numberOfLines={2}>
          {deal.product}
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
          <Text style={[s.price, tight && { fontSize: 15, letterSpacing: -0.4 }]}>{price(deal)}</Text>
          {off !== null ? (
            <Text style={[s.off, tight && { fontSize: 15 }]}>-{off}%</Text>
          ) : null}
        </View>
        <Text style={s.time}>
          {deal.shipping_krw === 0 ? '무료배송 · ' : ''}
          {deal.posted_at ? timeAgo(deal.posted_at) : ''}
        </Text>
      </View>
    </Pressable>
  );
}

const s = StyleSheet.create({
  head: { paddingHorizontal: 16, paddingBottom: 12, gap: 3 },
  brand: { fontSize: 25, fontWeight: '900', color: D.ink, letterSpacing: -0.8 },
  brandSub: { fontSize: 11, fontWeight: '600', color: D.ink2, letterSpacing: 0.2 },

  ask: { marginHorizontal: 16, marginBottom: 14, backgroundColor: D.ink, paddingVertical: 12, paddingHorizontal: 14, gap: 3 },
  askLabel: { fontSize: 10, fontWeight: '800', color: D.volt, letterSpacing: 1.3 },
  askBody: { fontSize: 14, fontWeight: '700', color: '#FFFFFF' },

  bar: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', paddingHorizontal: 16, paddingBottom: 12 },
  kicker: { fontSize: 10, fontWeight: '700', color: D.ink3, letterSpacing: 0.3 },
  h1: { fontSize: 22, fontWeight: '900', color: D.ink, letterSpacing: -0.5 },
  count: { fontSize: 11, fontWeight: '700', color: D.ink2, fontVariant: ['tabular-nums'] },
  colBtn: { borderWidth: 1, borderColor: D.ink, paddingVertical: 3, paddingHorizontal: 8, backgroundColor: D.volt },
  colBtnText: { fontSize: 11, fontWeight: '900', color: D.ink },

  nav: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, paddingHorizontal: 16, paddingBottom: 12 },
  chip: { paddingVertical: 6, paddingHorizontal: 11, backgroundColor: D.panel, borderWidth: 1, borderColor: D.ink },
  chipOn: { backgroundColor: D.ink },
  chipText: { fontSize: 11.5, fontWeight: '800', color: D.ink },
  chipTextOn: { color: D.volt },

  tile: { flex: 1, backgroundColor: D.panel, borderWidth: 1, borderColor: D.ink },
  shot: { aspectRatio: 1, backgroundColor: D.line, borderBottomWidth: 1, borderBottomColor: D.ink },
  noShot: { flex: 1, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 2, backgroundColor: D.ink },
  scoreNum: { fontSize: 42, fontWeight: '900', color: D.volt, letterSpacing: -2, fontVariant: ['tabular-nums'] },
  scoreUnit: { fontSize: 12, fontWeight: '800', color: D.volt, marginTop: 15 },
  scoreTag: { position: 'absolute', top: 0, right: 0, backgroundColor: D.ink, paddingVertical: 3, paddingHorizontal: 7 },
  scoreTagText: { fontSize: 12, fontWeight: '900', color: D.volt, fontVariant: ['tabular-nums'], letterSpacing: -0.2 },
  off: { fontSize: 17, fontWeight: '900', color: D.alert, letterSpacing: -0.5, fontVariant: ['tabular-nums'] },

  meta: { padding: 10, gap: 3 },
  mall: { fontSize: 9.5, fontWeight: '800', color: D.ink3, letterSpacing: 0.4 },
  name: { fontSize: 13, fontWeight: '700', color: D.ink, lineHeight: 17 },
  price: { fontSize: 18, fontWeight: '900', color: D.ink, letterSpacing: -0.5, fontVariant: ['tabular-nums'] },
  time: { fontSize: 9.5, fontWeight: '700', color: D.ink3, marginTop: 1 },

  empty: { paddingTop: 56, paddingHorizontal: 16, alignItems: 'center', gap: 6 },
  emptyTitle: { fontSize: 16, fontWeight: '800', color: D.ink },
  emptySub: { fontSize: 13, color: D.ink2, textAlign: 'center' },
  demo: { fontSize: 11, color: D.ink, backgroundColor: D.volt, paddingHorizontal: 10, paddingVertical: 6, overflow: 'hidden' },
  disclosure: { fontSize: 10.5, color: D.ink2, lineHeight: 16 },
});
